import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, BarChart3, TrendingUp, Users, Gift, Car, Megaphone, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Kpi({ label, value, sub, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-white/45 text-xs uppercase tracking-widest">{Icon && <Icon size={13} />}{label}</div>
      <div className="font-display font-black text-2xl mt-1.5 text-white">{value}</div>
      {sub && <div className="text-white/40 text-xs mt-0.5">{sub}</div>}
    </div>
  );
}

export default function BusinessIntelligence({ token }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    axios.get(`${API}/analytics/business-intelligence`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch(() => {});
  }, [token]);

  if (!d) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Crunching business intelligence…</div>;

  const maxRev = Math.max(...(d.monthly_trend || []).map((m) => m.revenue), 1);

  return (
    <div className="space-y-5" data-testid="business-intelligence">
      <h3 className="font-display font-bold text-white flex items-center gap-2"><BarChart3 size={17} className="text-[#F2B705]" /> Business Intelligence</h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="bi-abv" label="Avg Booking Value" value={money(d.avg_booking_value)} icon={TrendingUp} />
        <Kpi testid="bi-clv" label="Customer Lifetime Value" value={money(d.customer_lifetime_value)} sub={`${d.total_customers} customers`} icon={Users} />
        <Kpi testid="bi-repeat" label="Repeat Customer Rate" value={`${d.repeat_customer_rate}%`} icon={Users} />
        <Kpi testid="bi-referral" label="Referral Conversion" value={`${d.referral_conversion_rate}%`} sub={`${d.referral_bookings} bookings / ${d.referral_codes_issued} codes`} icon={Gift} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi testid="bi-best-channel" label="Best Channel" value={d.best_channel?.source || "—"} sub={d.best_channel ? `${d.best_channel.booked} booked · ${d.best_channel.conversion_pct}% conv` : "no lead data"} icon={Megaphone} />
        <Kpi testid="bi-best-vehicle" label="Best Vehicle" value={d.best_vehicle?.vehicle || "—"} sub={d.best_vehicle ? `${money(d.best_vehicle.revenue)} collected` : ""} icon={Car} />
        <Kpi testid="bi-best-segment" label="Most Profitable Segment" value={d.best_segment?.segment || "—"} sub={d.best_segment ? `${money(d.best_segment.avg_value)} avg per customer` : ""} icon={Crown} />
      </div>

      {/* Monthly growth trend */}
      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="bi-trend">
        <h4 className="font-display font-bold text-white mb-4">Monthly Growth Trend <span className="text-white/35 text-xs font-normal">(revenue collected)</span></h4>
        <div className="flex items-end gap-3 h-40">
          {(d.monthly_trend || []).map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full">
              <span className={`text-[11px] font-semibold ${m.growth_pct == null ? "text-white/40" : m.growth_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {m.growth_pct == null ? "" : `${m.growth_pct > 0 ? "+" : ""}${m.growth_pct}%`}
              </span>
              <div className="w-full max-w-[54px] bg-[#F2B705]/80 rounded-t" style={{ height: `${Math.max(4, (m.revenue / maxRev) * 100)}%` }} title={money(m.revenue)} />
              <span className="text-white/50 text-[11px]">{m.month.slice(2)}</span>
              <span className="text-white/70 text-[11px] font-semibold">{money(m.revenue)}</span>
            </div>
          ))}
          {(d.monthly_trend || []).length === 0 && <p className="text-white/40 text-sm m-auto">No revenue history yet.</p>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Channels */}
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="bi-channels">
          <h4 className="font-display font-bold text-white mb-3">Marketing Channels</h4>
          <table className="w-full text-sm">
            <thead><tr className="text-white/40 text-xs uppercase tracking-wider text-left border-b border-white/10">
              <th className="py-2 pr-3">Source</th><th className="py-2 pr-3 text-right">Leads</th><th className="py-2 pr-3 text-right">Booked</th><th className="py-2 text-right">Conv.</th></tr></thead>
            <tbody>
              {d.channels.map((c) => (
                <tr key={c.source} className="border-b border-white/5">
                  <td className="py-2 pr-3 text-white font-semibold">{c.source}</td>
                  <td className="py-2 pr-3 text-right text-white/60">{c.leads}</td>
                  <td className="py-2 pr-3 text-right text-white/80">{c.booked}</td>
                  <td className={`py-2 text-right ${c.conversion_pct > 0 ? "text-emerald-400" : "text-white/40"}`}>{c.conversion_pct}%</td>
                </tr>
              ))}
              {d.channels.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-white/40">No leads yet.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Segments + Vehicles */}
        <div className="space-y-4">
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="bi-segments">
            <h4 className="font-display font-bold text-white mb-3">Customer Segments</h4>
            <div className="space-y-2">
              {d.segments.map((s) => (
                <div key={s.segment} className={`flex items-center justify-between rounded-lg px-3 py-2.5 border ${d.best_segment?.segment === s.segment ? "border-[#F2B705]/40 bg-[#F2B705]/[0.06]" : "border-white/10 bg-black/30"}`}>
                  <span className="text-white/85 text-sm font-semibold">{s.segment} <span className="text-white/40 font-normal">· {s.customers}</span></span>
                  <span className="text-sm"><span className="text-white/50">{money(s.revenue)} total · </span><span className="text-emerald-400 font-semibold">{money(s.avg_value)} avg</span></span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="bi-vehicles">
            <h4 className="font-display font-bold text-white mb-3">Top Vehicles by Revenue</h4>
            <div className="space-y-1.5">
              {d.top_vehicles.slice(0, 6).map((v, i) => (
                <div key={v.vehicle} className="flex items-center gap-3 text-sm">
                  <span className={`w-5 text-center font-black ${i === 0 ? "text-[#F2B705]" : "text-white/40"}`}>{i + 1}</span>
                  <span className="text-white/85 mr-auto truncate">{v.vehicle}</span>
                  <span className="text-white/40 text-xs">{v.bookings} rentals</span>
                  <span className="text-emerald-400 font-semibold">{money(v.revenue)}</span>
                </div>
              ))}
              {d.top_vehicles.length === 0 && <p className="text-white/40 text-sm">No bookings yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
